import {
	getAlgoliaSearchParametersConfiguration,
	getInstantSearchClient,
} from "../../components/search/client";
import { initSearchBox } from "../../components/search/searchbox/searchbox";
import { initExtranetResults } from "../../components/search/extranet-results/extranet-results";

import {
	initClearRefinements,
	initCurrentRefinements,
} from "../../components/search/refinement-tags/refinement-tags";
import {
	customRefinementList,
	getRefinementList,
} from "../../components/search/refinement-list/refinement-list";
import {
	initStats,
	updateSearchSummary,
} from "../../components/search/result-bar/result-bar";
import { initSortBy } from "../../components/search/sort-by/connect-sort-by.js";
import { showLoader } from "../../components/search/loader/loader.js";

export class SubcategoryOverview extends HTMLElement {
	static #selectors = {
		refinementListsContainer: ".Refinements-filters",
		subcategoryOverviewData: "#subcategory-overview-data",
	};

	#elements;
	#instantSearch;

	#state = {
		refinementLists: [],
		subcategory: {},
	};

	/**
	 * Gets the elements required for the component.
	 * @private
	 * @returns {object} The elements required for the component.
	 */
	#getElements() {
		return {
			refinementListsContainer: this.querySelector(
				SubcategoryOverview.#selectors.refinementListsContainer,
			),
			subcategoryOverviewData: document.querySelector(
				SubcategoryOverview.#selectors.subcategoryOverviewData,
			),
		};
	}

	/**
	 * Creates an instance of SubcategoryOverview.
	 */
	constructor() {
		super();

		this.#elements = this.#getElements();

		if (!this.#elements.subcategoryOverviewData) {
			throw new Error("No subcategory overview data found.");
		}

		this.#setState();

		if (this.#state.refinementLists.length) {
			this.#appendRefinementListContainers();
		}

		this.#render();
	}

	/**
	 * Renders the subcategory overview component.
	 * @private
	 */
	async #render() {
		const { subcategory } = this.#state;

		const searchBoxOptions = {
			container: this.querySelector("search"),
			queryHook: (query, search) => {
				// This function is called when the user submits the search form.
				// Critically _not_ during the initial search on page load.
				updateSearchSummary(query);
				search(query);
			},
		};

		this.#instantSearch = await getInstantSearchClient();

		this.#instantSearch.addWidgets([
			initSearchBox(searchBoxOptions),
			initExtranetResults(),
			initCurrentRefinements(),
			initClearRefinements(),
			initStats(),
			initSortBy(".SortBy--mobile"),
			initSortBy(".SortBy"),
			getAlgoliaSearchParametersConfiguration(
				`subcategory:${subcategory.value}`,
			),
			...this.#getRefinementListWidgets(),
		]);

		this.#instantSearch.start();

		showLoader();

		updateSearchSummary(subcategory.label);
	}

	/**
	 * Sets the state of the component by parsing the subcategory overview data
	 * and storing it in localStorage.
	 * @private
	 */
	#setState() {
		const { subcategoryOverviewData } = this.#elements;
		const data = JSON.parse(subcategoryOverviewData.textContent);

		try {
			localStorage.setItem("subcategoryOverviewData", JSON.stringify(data));
			this.#state = { ...data };
		} catch (error) {
			throw new Error(`Error setting state in subcategory overview: ${error}`);
		}
	}

	/**
	 * Appends refinement list containers to the refinement lists container.
	 * @private
	 */
	#appendRefinementListContainers() {
		const { refinementLists } = this.#state;
		const { refinementListsContainer } = this.#elements;

		refinementLists.forEach((refinementList) => {
			const refinementListElement = getRefinementList(refinementList);
			refinementListsContainer.append(refinementListElement);
		});
	}

	/**
	 * Gets the refinement list widgets.
	 * @private
	 * @returns {Array} The refinement list widgets.
	 */
	#getRefinementListWidgets() {
		const { refinementLists } = this.#state;

		return refinementLists.map((facet) => {
			const { facet_key } = facet;
			const containerId = facet_key
				.split("_")
				.join("-")
				.concat("-refinement-list");
			return customRefinementList({
				attribute: facet_key,
				cssClasses: {
					count: "RefinementList-count",
				},
				container: `#${containerId}`,
			});
		});
	}
}

customElements.define("app-subcategory-overview", SubcategoryOverview);
